"use client";

import { usePathname } from "next/navigation";
import { type MouseEvent, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

import { BREAKPOINTS, getLinkTarget } from "@/globals";
import { isMD } from "@/globals.client";
import { BUTTON_STYLES } from "@/styles/buttons";
import { TYPO_STYLES } from "@/styles/typography";
import { VIEWPORT_STYLES } from "@/styles/viewport";
import { WRAPPER_STYLES } from "@/styles/wrapper";

import { type NavigationItem } from "./scheme";

type NavigationBottomProps = {
    isParentNavigationItemSelected?: boolean;
    navigationItems: NavigationItem[] | undefined;
    navigationLevel: number;
    onLevelClick: (level: number) => void;
    parentNavigationItem?: NavigationItem;
    selectedNavigationLevel: number;
};

export const NavigationBottomView = ({
    onLevelClick,
    navigationItems,
    navigationLevel,
    parentNavigationItem,
    isParentNavigationItemSelected,
    selectedNavigationLevel,
}: NavigationBottomProps) => {
    const [selectedId, setSelectedId] = useState<string | undefined>();

    const currentPath = usePathname();

    const handleBackClick = (e: MouseEvent<HTMLElement>): void => {
        e.preventDefault();
        onLevelClick(navigationLevel - 1);
    };

    const handleItemClick = (e: MouseEvent<HTMLElement>, navigationItem: NavigationItem): void => {
        if (navigationItem.navigationItems?.length && !isMD()) {
            e.preventDefault();
            setSelectedId(navigationItem.id);
            onLevelClick(navigationLevel + 1);
        }
    };

    const handleLevelClick = (level: number): void => {
        onLevelClick?.(level);
    };

    useEffect(() => {
        if (selectedNavigationLevel <= navigationLevel) {
            setSelectedId(undefined);
        }
    }, [navigationLevel, selectedNavigationLevel]);

    if (navigationItems?.length) {
        return (
            <div
                className={twMerge(
                    "top-0 w-full left-full md:left-0 md:top-auto",
                    "navigation-level-" + navigationLevel,
                    navigationLevel === 0 ? "static" : "absolute",
                    navigationLevel === 0 || isParentNavigationItemSelected ? "block" : "hidden",
                    "md:shadow-md",
                    navigationLevel > 0 ? "md:py-4" : "",
                )}
            >
                <div className={twMerge(VIEWPORT_STYLES.viewport())}>
                    <ul
                        className={twMerge(
                            "-mx-4",
                            WRAPPER_STYLES.VCenterLeft(BREAKPOINTS.md),
                            navigationLevel > 1 ? "" : WRAPPER_STYLES.HCenterLeft(BREAKPOINTS.md),
                            "md:justify-start",
                        )}
                    >
                        {navigationLevel > 0 && (
                            <li className="md:hidden bg-grey1 w-full md:w-auto">
                                <button
                                    onClick={(e) => handleBackClick(e)}
                                    className={BUTTON_STYLES.headerNavigationBack()}
                                >
                                    <span
                                        aria-hidden="true"
                                        className={twMerge(TYPO_STYLES.icon(), "text-2xl pr-2")}
                                    >
                                        {String.fromCharCode(Number("0xe907"))}
                                    </span>
                                    <span>{parentNavigationItem?.label}</span>
                                </button>
                            </li>
                        )}
                        {parentNavigationItem?.href && (
                            <li
                                className={twMerge(
                                    navigationLevel < 2 ? "md:hidden" : "md:block",
                                    "w-full md:w-auto",
                                )}
                            >
                                <a
                                    href={parentNavigationItem.href}
                                    className={BUTTON_STYLES.headerNavigationOverview()}
                                >
                                    <span
                                        aria-hidden="true"
                                        className={twMerge(TYPO_STYLES.icon(), "text-2xl pr-2")}
                                    >
                                        {String.fromCharCode(Number("0xe938"))}
                                    </span>
                                    <span>Übersicht</span>
                                </a>
                            </li>
                        )}
                        {navigationItems.map((navigationItem, i) => {
                            const hasSubnavigation = !!navigationItem.navigationItems?.length;

                            let buttonClass = undefined;

                            if (navigationLevel === 0) {
                                buttonClass = BUTTON_STYLES.headerNavigationSecondary(
                                    navigationItem.href === currentPath,
                                );
                            } else if (hasSubnavigation) {
                                buttonClass = BUTTON_STYLES.headerNavigationTab(
                                    navigationItem.id === selectedId ||
                                        navigationItem.href === currentPath,
                                );
                            } else {
                                buttonClass = BUTTON_STYLES.headerNavigationPrimary(
                                    navigationItem.href === currentPath,
                                );
                            }

                            return (
                                <li key={i} className={twMerge("w-full md:w-auto")}>
                                    <a
                                        target={getLinkTarget(navigationItem.href)}
                                        href={navigationItem.href || "#"}
                                        onClick={(e) => handleItemClick(e, navigationItem)}
                                        className={twMerge(
                                            buttonClass,
                                            hasSubnavigation
                                                ? WRAPPER_STYLES.HCenterBetween()
                                                : WRAPPER_STYLES.HCenterLeft(),
                                            WRAPPER_STYLES.HCenterLeft(BREAKPOINTS.md),
                                        )}
                                    >
                                        {navigationItem.icon && (
                                            <span
                                                aria-hidden="true"
                                                className={twMerge(
                                                    TYPO_STYLES.icon(),
                                                    "text-2xl pr-2",
                                                )}
                                            >
                                                {String.fromCharCode(
                                                    Number("0x" + navigationItem.icon),
                                                )}
                                            </span>
                                        )}
                                        {navigationItem.label && (
                                            <span>{navigationItem.label}</span>
                                        )}

                                        {hasSubnavigation ? (
                                            <span
                                                aria-hidden="true"
                                                className={twMerge(
                                                    TYPO_STYLES.icon(),
                                                    "text-2xl pl-2 md:hidden",
                                                )}
                                            >
                                                {String.fromCharCode(Number("0xe918"))}
                                            </span>
                                        ) : undefined}
                                    </a>
                                    <NavigationBottomView
                                        parentNavigationItem={navigationItem}
                                        navigationItems={navigationItem.navigationItems}
                                        isParentNavigationItemSelected={
                                            navigationItem.id === selectedId
                                        }
                                        navigationLevel={navigationLevel + 1}
                                        onLevelClick={handleLevelClick}
                                        selectedNavigationLevel={selectedNavigationLevel}
                                    />
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        );
    } else {
        return null;
    }
};
